<template>
    <div 
        class="flow-object-team-list-single"
        :class="{
            'is-group': isGroup,
            'is-open-group': isGroup && !isCollapsed,
            'is-role': isRole,
        }"
    >
        <template v-if="isRole">
            <div class="d-flex justify-content-between">
                <div class="">
                    <span class="" :style="nameStyles">{{ roleOrGroup.name }}</span>
                    <base-popover-icon
                        v-if="roleOrGroup.description"
                        :title="roleOrGroup.name"
                        :content="roleOrGroup.description"
                        icon="info-circle"
                        class="ml-2"
                    ></base-popover-icon>
                </div>
                <div class="">
                    <b-link v-if="roleOrGroup.can_add" class="special-text text-decoration-none" href="#" @click="startAdd(roleOrGroup)">+ Add</b-link>
                </div>
            </div>
            <hr class="mb-2 mt-1">
            <div class="">
                <div v-if="status === 'loading'" key="loading">
                    <b-skeleton width="50%"></b-skeleton>
                </div>
                <div v-else-if="status === 'empty'" key="empty" class="text-muted font-size-80">{{ roleOrGroup.empty_text }}</div>
                <div v-else-if="status === 'loaded'" key="roleLoaded" class="d-flex gap-2 flex-wrap">
                    <tenant-user-badge
                        v-for="tu in roleOrGroup.tenant_users" 
                        :key="tu.id"
                        :tu-data="tu"
                        :deselectable="roleOrGroup.can_remove"
                        @deselect="handleRemoveTu($event, roleOrGroup)"
                    ></tenant-user-badge>
                    <!-- @deselect is emitted when you click on it when it is deselectable, payload is TU obj -->
                </div>
            </div>
        </template>
        <template v-else-if="isGroup">
            <div class="bg-gray-50">
                <b-button block variant="collapse-controller" @click="toggleCollapse">
                    <div class="d-flex justify-content-between">
                        <div class="">
                            <b-icon v-if="roleOrGroup.is_collapsible" :icon="isCollapsed ? 'chevron-right' : 'chevron-down'" class="mr-2"></b-icon>
                            <span class="text-black" :style="nameStyles">{{ roleOrGroup.name }}</span>
                            <base-popover-icon
                                v-if="roleOrGroup.description"
                                :title="roleOrGroup.name"
                                :content="roleOrGroup.description"
                                icon="info-circle"
                                class="ml-2 font-size-70"
                            ></base-popover-icon>
                        </div>
                        <div class="">
                            <span class="special-text text-muted">{{ childCountText }}</span>
                        </div>
                    </div>
                </b-button>
            </div>
            <div class="">
                <div v-if="status === 'loading'" key="loading">
                    <b-skeleton width="50%"></b-skeleton>
                </div>
                <div v-else-if="status === 'empty'" key="empty" class="text-muted font-size-80">{{ roleOrGroup.empty_text }}</div>
                <b-collapse v-else-if="status === 'loaded'" key="groupLoaded" :visible="!isCollapsed" class="px-3">
                    <div class="pt-3">
                        <flow-object-team-list-single-role
                            v-for="role in roleOrGroup.roles"
                            :key="role.unique_key"
                            :role-or-group="role"
                            :loading-tus="loadingTus"
                            :handle-remove-tu="handleRemoveTu"
                            :start-add="startAdd"
                            :level="childLevel"
                            class="mb-3"
                        ></flow-object-team-list-single-role>
                    </div>
                </b-collapse>
            </div>
        </template>
    </div>
</template>

<script>
import { RoleOrGroupDisplay } from './teamListUtils.js';

// this is just a child component for FlowObjectTeamList.vue to handle a single role OR group
// it needs to be recursive to handle roles or roles within groups

export default {
    name: 'FlowObjectTeamListSingleRole',
    components: {},
    mixins: [],
    props: {
        roleOrGroup: { type: RoleOrGroupDisplay, required: true },  // instance of RoleOrGroupDisplay 
        loadingTus: { type: Boolean, default: false },
        handleRemoveTu: { type: Function, required: true },
        startAdd: { type: Function, required: true },
        level: { type: Number, default: 0 },
        isCollapsed: { type: Boolean, default: false },  // only for groups
    },
    data() {
        return {
            nameStylesByLevel: { // there should only be 0 and 1 levels here
                0: {'font-size': '1em', 'font-weight': '700'},
                1: {'font-size': '0.9em', 'font-weight': '600'},
            },
        }
    },
    computed: {
        isGroup() {
            return this.roleOrGroup.role_or_group === 'group';
        },
        isRole() {
            return this.roleOrGroup.role_or_group === 'role';
        },
        status() {
            // are we loading?  empty?  loaded?
            if (this.loadingTus) {
                return 'loading';
            } else if (this.isRole && (!this.roleOrGroup.tenant_users || this.roleOrGroup.tenant_users.length < 1)) {
                return 'empty';
            } else if (this.isGroup && (!this.roleOrGroup.roles || this.roleOrGroup.roles.length < 1)) {
                return 'empty';
            } else {
                return 'loaded';
            }
        },
        childLevel() {
            return this.level + 1;
        },
        nameStyles() {
            return this.nameStylesByLevel[this.level] || this.nameStylesByLevel[1];
        },
        childCountText() {
            // used for groups only
            if (this.isGroup && this.roleOrGroup.roles) {
                const count = this.roleOrGroup.roles.length;
                return count === 1 ? '1 role' : `${count} roles`;
            }
            return '';
        },
    },
    created() {
        
    },
    mounted() {
        
    },
    methods: {
        toggleCollapse() {
            if (!this.roleOrGroup.is_collapsible) {
                return;  // do nothing if not collapsible
            }
            console.log('toggleCollapse from the group called for', this.roleOrGroup.unique_key);
            this.$emit('toggle-collapse', this.roleOrGroup.unique_key);
        },
    },
}
</script>

<style lang="scss" scoped>

.flow-object-team-list-single {
    &.is-group {
        // background-color: var(--gray-50);  // keep it white for cleaner look with TU badges
        border-radius: 0.375rem; // rounded
        overflow: hidden;
        border: 1px solid var(--gray-200);
        &.is-open-group {
            // box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);  // this is a nice shadow, but if we have default open groups, it gets too busy
        }
    }
    &.is-role {
        // no special styles for roles yet   
    }
}

</style>